/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	ISchemaConstMapModel,
	ISchemaConstMapModelEntry,
	ISchemaFlowNodeListModel,
	ISchemaFlowNodeModel,
	ISchemaFlowNodeTypeDefinitionMap,
	ISelectionItem_Base,
	isSchemaConstMapModel,
	isSchemaConstMapModelEntry,
	Selection,
	TGenericBlueprintSchema
} from "@adapptio/lib-blueprint";
import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { TSelectableSchemaBuilder, TViewEditSelectionItem } from "../../ViewEditor/Common/ViewEditSelection";

export enum ACTION_SELECTION_ITEM_TYPE {
	FLOW_NODE = "flowNode",
	PARAMETER = "parameter"
}

export interface ISelectionItem_FlowNode extends ISelectionItem_Base {
	/** Item type */
	type: ACTION_SELECTION_ITEM_TYPE.FLOW_NODE;
	nodeListModelId: number;
}

export interface ISelectionItem_Parameter extends ISelectionItem_Base {
	type: ACTION_SELECTION_ITEM_TYPE.PARAMETER;
	parentMapModelId: number;
	mapEntry: ISchemaConstMapModelEntry<TGenericBlueprintSchema>;
}

export type TActionEditSelectionItem = ISelectionItem_FlowNode | ISelectionItem_Parameter;

/**
 * Type guard for flow node selection
 * @param value
 */
export function isSelectionItemActionFlowNode(
	value: TViewEditSelectionItem
): value is ISelectionItem_FlowNode {
	return value?.type === ACTION_SELECTION_ITEM_TYPE.FLOW_NODE;
}

/**
 * Type guard for parameter selection
 * @param value
 */
export function isSelectionItemActionParameter(
	value: TViewEditSelectionItem
): value is ISelectionItem_Parameter {
	return value?.type === ACTION_SELECTION_ITEM_TYPE.PARAMETER;
}

/**
 * Class to manage action model selections
 */
export class ActionEditSelection extends Selection<TActionEditSelectionItem> {
	/** When there's a try to select already selected item */
	public onSelectSelected = createEventEmitter<void>();

	/**
	 * Returns if flow node is selected
	 *
	 * @param nodeModel Flow Node model
	 */
	public isFlowNodeSelected(nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>): boolean {
		if (!nodeModel) {
			return false;
		}

		const index = this.findItemIndexByNodeId(nodeModel.nodeId);
		return index >= 0 && this.items[index].type === ACTION_SELECTION_ITEM_TYPE.FLOW_NODE;
	}

	/**
	 * Selects a flow node
	 * Return if the flow node was (de)selected or not
	 *
	 * @param nodeModel Flow Node model
	 */
	public selectFlowNode(
		nodeList: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
		nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>
	): boolean {
		if (!nodeList || !nodeModel) {
			return false;
		}

		const isSelected = this.isFlowNodeSelected(nodeModel);

		// In multiselect, de-selected the component, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectFlowNode(nodeModel);
			return true;
		} else if (isSelected) {
			emitEvent(this.onSelectSelected);
			return false;
		}

		const selItem: ISelectionItem_FlowNode = {
			type: ACTION_SELECTION_ITEM_TYPE.FLOW_NODE,
			nodeId: nodeModel.nodeId,
			nodeListModelId: nodeList.nodeId
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Deselected the data source (if was selected) and returns if selection changes
	 *
	 * @param dsModel DataSource model
	 */
	public deselectFlowNode(nodeModel: ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>): boolean {
		if (!nodeModel) {
			return false;
		}

		return this.deselectByNodeId(nodeModel.nodeId);
	}

	/**
	 * Sets the selection of flow nodes (replaces current selection entirely)
	 *
	 * @param nodeModels Flow node models to select
	 */
	public setFlowNodeSelection(
		nodeList: ISchemaFlowNodeListModel<ISchemaFlowNodeTypeDefinitionMap>,
		nodeModels: Array<ISchemaFlowNodeModel<ISchemaFlowNodeTypeDefinitionMap>>
	): void {
		// De-select previous items if not present in a new array
		const newIdList = nodeModels.map((item) => item.nodeId);
		const oldIdList = this.items.map((item) => item.nodeId);

		for (let i = 0; i < this.items.length; i++) {
			if (!newIdList.includes(this.items[i].nodeId)) {
				emitEvent(this.onUnselectSingle, this.items[i]);
			}
		}

		// Set new selection
		this.items = nodeModels.map((item) => ({
			type: ACTION_SELECTION_ITEM_TYPE.FLOW_NODE,
			nodeId: item.nodeId,
			nodeListModelId: nodeList.nodeId
		}));

		// Emit events for newly selected items
		this.items.forEach((item) => {
			if (!oldIdList.includes(item.nodeId)) {
				emitEvent(this.onSelectSingle, item);
			} else {
				emitEvent(this.onSelectSelected);
			}
		});

		if (this.items.length === 0) {
			emitEvent(this.onUnselectAll);
		}

		emitEvent(this.onChange);
	}

	/**
	 * Deselected all
	 */
	public deselectAll() {
		this.unselectAll();
		emitEvent(this.onChange);
	}

	/**
	 * Selects parameter model
	 * Returns true if parameter was (de)selected or not
	 */
	public selectParameter(
		parentModel: ISchemaConstMapModel<TSelectableSchemaBuilder>,
		mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>
	): boolean {
		if (!isSchemaConstMapModel(parentModel)) {
			return false;
		}

		const isSelected = this.isParameterSelected(parentModel, mapEntry);

		// In multiselect, de-selected the item, otherwise it's already selected so do nothing
		if (isSelected && this.multiSelect) {
			this.deselectParameter(parentModel, mapEntry);
			return true;
		} else if (isSelected) {
			return false;
		}

		const selItem: ISelectionItem_Parameter = {
			type: ACTION_SELECTION_ITEM_TYPE.PARAMETER,
			nodeId: mapEntry.key.nodeId,
			parentMapModelId: parentModel.nodeId,
			mapEntry: mapEntry
		};

		if (this.multiSelect) {
			this.items.push(selItem);
		} else {
			this.unselectAll();
			this.items = [ selItem ];
		}

		emitEvent(this.onSelectSingle, selItem);
		emitEvent(this.onChange);

		return true;
	}

	/**
	 * Returns true if parameter is selected
	 */
	public isParameterSelected(
		parentModel: ISchemaConstMapModel<TSelectableSchemaBuilder>,
		mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		const index = this.findItemIndexByNodeId(mapEntry.key.nodeId);
		const item = this.items[index];

		if (isSelectionItemActionParameter(item)) {
			return item.parentMapModelId === parentModel.nodeId;
		}

		return false;
	}

	/**
	 * Deselect the parameter (if was selected) and return true if selection changes
	 */
	public deselectParameter(
		parentModel: ISchemaConstMapModel<TSelectableSchemaBuilder>,
		mapEntry: ISchemaConstMapModelEntry<TSelectableSchemaBuilder>
	): boolean {
		if (!isSchemaConstMapModel(parentModel) || !isSchemaConstMapModelEntry(mapEntry)) {
			return false;
		}

		if (!this.isParameterSelected(parentModel, mapEntry)) {
			return false;
		}

		return this.deselectByNodeId(mapEntry.key.nodeId);
	}
}
